import React from "react";
import { ChevronsUpDown } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

export interface SelectionComboboxOption {
  value: string;
  label: string;
}

export interface SelectionComboboxProps {
  placeholder: string;
  options: SelectionComboboxOption[];
  emptyMessage?: string;
  onSelect: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

export const SelectionCombobox: React.FC<SelectionComboboxProps> = ({
  placeholder,
  options,
  emptyMessage = "No results",
  onSelect,
  loading,
  disabled,
  searchPlaceholder = "Search...",
  triggerClassName,
  contentClassName,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) {
      return;
    }
    setOpen(nextOpen);
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger className="w-full">
          <Button
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            variant="outline"
            className={cn(
              "inline-flex h-9 w-full items-center justify-between gap-2 px-3",
              triggerClassName
            )}
          >
            <span className="truncate text-left text-sm font-normal text-muted-foreground">
              {placeholder}
            </span>
            <ChevronsUpDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn(
            "w-[var(--combobox-width,16rem)] p-0",
            contentClassName
          )}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="h-9" />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => {
                        onSelect(option.value);
                        setOpen(false);
                      }}
                    >
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
